import axiosWithAuth from '../utils/axiosWithAuth';

// Content Review Service
export const getContentForReview = async (propertyId, params = {}) => {
    try {
        const response = await axiosWithAuth.get(`/cas/content-review/${propertyId}`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching content for review:', error);
        throw error;
    }
};

export const updateContentReview = async (propertyId, contentId, updateData) => {
    try {
        const response = await axiosWithAuth.put(`/cas/content-review/${propertyId}/content/${contentId}`, updateData);
        return response.data;
    } catch (error) {
        console.error('Error updating content review:', error);
        throw error;
    }
};

export const bulkReviewContent = async (contentIds, reviewNotes = '') => {
    try {
        const response = await axiosWithAuth.post('/cas/content-review/bulk-review', {
            content_ids: contentIds,
            review_notes: reviewNotes
        });
        return response.data;
    } catch (error) {
        console.error('Error bulk reviewing content:', error);
        throw error;
    }
};

export const bulkRejectContent = async (contentIds, rejectReason = '') => {
    try {
        const response = await axiosWithAuth.post('/cas/content-review/bulk-reject', {
            content_ids: contentIds,
            reject_reason: rejectReason
        });
        return response.data;
    } catch (error) {
        console.error('Error bulk rejecting content:', error);
        throw error;
    }
};

export const bulkDownloadContent = async (contentIds, format = 'txt') => {
    try {
        const response = await axiosWithAuth.post('/cas/content-review/bulk-download', {
            content_ids: contentIds,
            format: format
        }, {
            responseType: 'blob',
            headers: {
                'Accept': 'application/zip, application/octet-stream'
            }
        });
        return response.data;
    } catch (error) {
        console.error('Error bulk downloading content:', error);
        throw error;
    }
};

export const bulkPublishContent = async (contentIds, propertyId, platform = 'wordpress') => {
    try {
        const response = await axiosWithAuth.post('/cas/content-review/bulk-publish', {
            content_ids: contentIds,
            property_id: propertyId,
            platform: platform,
            batch_id: `bulk_publish_${Date.now()}`
        });
        return response.data;
    } catch (error) {
        console.error('Error bulk publishing content:', error);
        throw error;
    }
};

// Property Service
export const getUserCASProperties = async () => {
    try {
        const response = await axiosWithAuth.get('/cas/properties');
        return response;
    } catch (error) {
        console.error('Error fetching user CAS properties:', error);
        throw error;
    }
};

export const fetchUserProperties = async () => {
    try {
        const response = await axiosWithAuth.get('/cas/properties');
        return response.data;
    } catch (error) {
        console.error('Error fetching user properties:', error);
        throw error;
    }
};

export const fetchAvailableGoogleProperties = async () => {
    try {
        const response = await axiosWithAuth.get('/cas/properties/available-google-properties');
        return response.data;
    } catch (error) {
        console.error('Error fetching available Google properties:', error);
        throw error;
    }
};

export const createManagedProperty = async (propertyData) => {
    try {
        const response = await axiosWithAuth.post('/cas/properties', propertyData);
        return response.data;
    } catch (error) {
        console.error('Error creating managed property:', error);
        throw error;
    }
};

export const fetchManagedPropertyById = async (id) => {
    try {
        const response = await axiosWithAuth.get(`/cas/properties/${id}`);
        return response.data;
    } catch (error) {
        console.error(`Error fetching managed property ${id}:`, error);
        throw error;
    }
};

export const updateManagedProperty = async (id, updateData) => {
    try {
        const response = await axiosWithAuth.put(`/cas/properties/${id}`, updateData);
        return response.data;
    } catch (error) {
        console.error(`Error updating managed property ${id}:`, error);
        throw error;
    }
};

export const deleteManagedProperty = async (id) => {
    try {
        const response = await axiosWithAuth.delete(`/cas/properties/${id}`);
        return response.data;
    } catch (error) {
        console.error(`Error deleting managed property ${id}:`, error);
        throw error;
    }
};

// External Content Service
export const createExternalBlog = async (blogData) => {
    try {
        const response = await axiosWithAuth.post('/cas/external/blog-generation', blogData);
        return response.data;
    } catch (error) {
        console.error('Error creating external blog:', error);
        throw error;
    }
};

export const createExternalPage = async (pageData) => {
    try {
        const response = await axiosWithAuth.post('/cas/external/page-creation', pageData);
        return response.data;
    } catch (error) {
        console.error('Error creating external page:', error);
        throw error;
    }
};

export const createExternalImageGeneration = async (imageData) => {
    try {
        const response = await axiosWithAuth.post('/cas/external/image-generation', imageData);
        return response.data;
    } catch (error) {
        console.error('Error creating external image generation:', error);
        throw error;
    }
};

// WordPress Configuration Service
export const getWordPressConfig = async (propertyId) => {
    try {
        const response = await axiosWithAuth.get(`/cas/wordpress-config/property/${propertyId}`);
        return response.data;
    } catch (error) {
        console.error('Error fetching WordPress config:', error);
        throw error;
    }
};

export const createWordPressConfig = async (configData) => {
    try {
        const response = await axiosWithAuth.post('/cas/wordpress-config', configData);
        return response.data;
    } catch (error) {
        console.error('Error creating WordPress config:', error);
        throw error;
    }
};

export const updateWordPressConfig = async (configId, configData) => {
    try {
        const response = await axiosWithAuth.put(`/cas/wordpress-config/${configId}`, configData);
        return response.data;
    } catch (error) {
        console.error('Error updating WordPress config:', error);
        throw error;
    }
};

// Business Profile Service
export const getBusinessProfile = async (propertyId) => {
    try {
        const response = await axiosWithAuth.get(`/cas/properties/${propertyId}/profile`);
        return response.data;
    } catch (error) {
        console.error('Error fetching business profile:', error);
        throw error;
    }
};

export const updateBusinessProfile = async (propertyId, profileData) => {
    try {
        const response = await axiosWithAuth.put(`/cas/properties/${propertyId}/profile`, profileData);
        return response.data;
    } catch (error) {
        console.error('Error updating business profile:', error);
        throw error;
    }
};

export const createBusinessProfile = async (propertyId) => {
    try {
        const response = await axiosWithAuth.post(`/cas/properties/${propertyId}/profile`);
        return response.data;
    } catch (error) {
        console.error('Error creating business profile:', error);
        throw error;
    }
};

// Content Management Service
export const getContentManagement = async (propertyId, params = {}) => {
    try {
        const response = await axiosWithAuth.get(`/cas/content-management/${propertyId}`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching content management:', error);
        throw error;
    }
};

export const updateContentManagement = async (propertyId, contentId, updateData) => {
    try {
        const response = await axiosWithAuth.put(`/cas/content-management/${propertyId}/content/${contentId}`, updateData);
        return response.data;
    } catch (error) {
        console.error('Error updating content management:', error);
        throw error;
    }
};

// Rewrite Cycles Service
export const getRewriteCycles = async (propertyId, params = {}) => {
    try {
        const response = await axiosWithAuth.get(`/cas/rewrite-cycles/${propertyId}`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching rewrite cycles:', error);
        throw error;
    }
};

export const createRewriteCycle = async (propertyId, cycleData) => {
    try {
        const response = await axiosWithAuth.post(`/cas/rewrite-cycles/${propertyId}`, cycleData);
        return response.data;
    } catch (error) {
        console.error('Error creating rewrite cycle:', error);
        throw error;
    }
};

// Analysis Items Service
export const getAnalysisItems = async (propertyId, params = {}) => {
    try {
        const response = await axiosWithAuth.get(`/cas/analysis-items/${propertyId}`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching analysis items:', error);
        throw error;
    }
};

export const getAdminAnalysisItemsAll = async (params = {}) => {
    try {
        const response = await axiosWithAuth.get('/cas/analysis-items/admin/all', { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching admin analysis items:', error);
        throw error;
    }
};

// Rewritten Content Service
export const getRewrittenContent = async (propertyId, params = {}) => {
    try {
        const response = await axiosWithAuth.get(`/cas/rewritten-content/${propertyId}`, { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching rewritten content:', error);
        throw error;
    }
};

// Health Check Service
export const checkCASHealth = async () => {
    try {
        const response = await axiosWithAuth.get('/cas/health');
        return response.data;
    } catch (error) {
        console.error('Error checking CAS health:', error);
        throw error;
    }
};

export const startBatchProcessing = async (itemIds, processingMode = 'sequential', batchSize = null) => {
    try {
        const response = await axiosWithAuth.post('/cas/analysis-items/batch/process', {
            item_ids: itemIds,
            processing_mode: processingMode,
            batch_size: batchSize,
        });
        return response.data;
    } catch (error) {
        console.error('Error starting batch processing:', error);
        throw error;
    }
};

export const batchUpdateStatus = async (itemIds, action) => {
    try {
        const response = await axiosWithAuth.post('/cas/analysis-items/batch/status', {
            item_ids: itemIds,
            action,
        });
        return response.data;
    } catch (error) {
        console.error('Error batch updating status:', error);
        throw error;
    }
};

export const getBatchJobs = async (params = {}) => {
    try {
        const response = await axiosWithAuth.get('/cas/analysis-items/batch', { params });
        return response.data;
    } catch (error) {
        console.error('Error fetching batch jobs:', error);
        throw error;
    }
};

export const triggerBatchPoll = async (batchId) => {
    try {
        const response = await axiosWithAuth.post(`/cas/analysis-items/batch/${batchId}/trigger-poll`);
        return response.data;
    } catch (error) {
        console.error('Error triggering batch poll:', error);
        throw error;
    }
};

export const adminTriggerAnalysis = async (propertyId) => {
    try {
        const response = await axiosWithAuth.post(`/cas/properties/${propertyId}/trigger-analysis`);
        return response.data;
    } catch (error) {
        console.error('Error triggering analysis:', error);
        throw error;
    }
}; 